import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('da_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      // token invalid -> clear
      if (localStorage.getItem('da_token')) {
        localStorage.removeItem('da_token');
        localStorage.removeItem('da_user');
      }
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  register: (data) => api.post('/auth/register', data).then(r => r.data),
  verifyEmail: (data) => api.post('/auth/verify-email', data).then(r => r.data),
  resendCode: (email) => api.post('/auth/resend-code', { email }).then(r => r.data),
  login: (data) => api.post('/auth/login', data).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  linkWallet: (walletAddress) => api.post('/auth/link-wallet', { walletAddress }).then(r => r.data),
  updateProfile: (data) => api.post('/auth/update-profile', data).then(r => r.data),
  changePassword: (data) => api.post('/auth/change-password', data).then(r => r.data),
  passwordResetRequest: (data) => api.post('/auth/password-reset/request', data).then(r => r.data),
  passwordResetConfirm: (data) => api.post('/auth/password-reset/confirm', data).then(r => r.data),
  myTransactions: () => api.get('/me/transactions').then(r => r.data),
};

export const presaleApi = {
  state: () => api.get('/presale/state').then(r => r.data),
  estimate: (amount_usd) => api.post('/presale/estimate', { amount_usd }).then(r => r.data),
};

export const depositApi = {
  create: (data) => api.post('/deposit', data).then(r => r.data),
  status: (paymentId) => api.get(`/deposit/${paymentId}`).then(r => r.data),
};

export const withdrawApi = {
  withdraw: (data) => api.post('/withdraw', data).then(r => r.data),
};

export const phantomDepositApi = {
  info: () => api.get('/deposit/phantom/info').then(r => r.data),
  confirm: (signature, expected_amount) => api.post('/deposit/phantom/confirm', { signature, expected_amount }).then(r => r.data),
};

export const referralApi = {
  me: () => api.get('/referral/me').then(r => r.data),
  resolve: (username) => api.get(`/referral/resolve/${encodeURIComponent(username)}`).then(r => r.data),
  earnings: (limit = 30) => api.get(`/referral/earnings?limit=${limit}`).then(r => r.data),
};

export const gameApi = {
  startMatch: (data) => api.post('/game/match/start', data).then(r => r.data),
  settleMatch: (data) => api.post('/game/match/settle', data).then(r => r.data),
  stats: () => api.get('/game/stats').then(r => r.data),
  history: (limit = 100) => api.get(`/game/history?limit=${limit}`).then(r => r.data),
};

export const googleAuthApi = {
  callback: (session_id, referredBy) => api.post('/auth/google-callback', { session_id, referredBy }).then(r => r.data),
};

export const walletsApi = {
  status: () => api.get('/wallets/status').then(r => r.data),
};

export const REF_STORAGE_KEY = 'da_ref';

export const captureReferralFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get('ref') || params.get('r') || '').trim();
    if (ref) {
      localStorage.setItem(REF_STORAGE_KEY, ref);
      // Clean the URL so the ref param doesn't stick around
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      url.searchParams.delete('r');
      window.history.replaceState({}, '', url.toString());
    }
  } catch (e) { /* ignore */ }
};

export const getStoredReferral = () => {
  try { return localStorage.getItem(REF_STORAGE_KEY) || null; } catch (e) { return null; }
};

export const clearStoredReferral = () => {
  try { localStorage.removeItem(REF_STORAGE_KEY); } catch (e) { /* ignore */ }
};

export const airdropApi = {
  info: () => api.get('/airdrop/info').then(r => r.data),
  clickTask: (task) => api.post('/airdrop/click-task', { task }).then(r => r.data),
  startTask: (task) => api.post('/airdrop/start-task', { task }).then(r => r.data),
  verifyTask: (task, proof) => api.post('/airdrop/verify-task', { task, proof }).then(r => r.data),
  claim: () => api.post('/claim-airdrop').then(r => r.data),
};

export const adminApi = {
  stats: () => api.get('/admin/stats').then(r => r.data),
  users: (params = {}) => api.get('/admin/users', { params: { limit: 50, ...params } }).then(r => r.data),
  transactions: (limit = 50) => api.get('/admin/transactions', { params: { limit } }).then(r => r.data),
  deposits: (limit = 50) => api.get('/admin/deposits', { params: { limit } }).then(r => r.data),
  blockUser: (userId, blocked, reason) => api.post(`/admin/users/${userId}/block`, { blocked, reason }).then(r => r.data),
  airdropDetail: (userId) => api.get(`/admin/users/${userId}/airdrop`).then(r => r.data),
  airdropAdjust: (userId, payload) => api.post(`/admin/users/${userId}/airdrop`, payload).then(r => r.data),
  pendingDeposits: () => api.get('/admin/pending-deposits').then(r => r.data),
  recreditDeposit: (paymentId) => api.post(`/admin/recredit-deposit/${paymentId}`).then(r => r.data),
  fraudAlerts: () => api.get('/admin/fraud-alerts').then(r => r.data),
  creditUser: (payload) => api.post('/admin/credit-user', payload).then(r => r.data),
  userGameHistory: (userId, limit = 100) => api.get(`/admin/users/${userId}/game-history?limit=${limit}`).then(r => r.data),
};

export default api;
